import {
  CavityWallConfigurationState,
  FE_MATERIALS,
  cavityWallMaterialMap,
  WallTypeTypes,
  WallInsulationTypes,
  CavityWallThermalPerformanceState,
  FEMaterial,
  OuterCavityLeafTypes,
  CAVITY_WALL_MINIMUM_INSULATION_THICKNESS,
} from "@iko-design-center/shared"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { DownloadState } from "./download"
import { initialCavityWallState } from "./state"
import { getThermalPerformance, updateThermalLayer } from "./thermalPerformance"

export type CavityWallData = {
  wallType: {
    type: any
    material: FEMaterial | null
  }
  wallInsulation: {
    type: any
    material: FEMaterial | null
  }
  outerCavityLeaf: {
    type: any
    material: FEMaterial | null
  }
  thermalPerformance: CavityWallThermalPerformanceState
}

export const cavityWallSlice = createSlice({
  name: "cavityWall",
  initialState: initialCavityWallState,
  reducers: {
    resetConfiguration: (state) => {
      return {
        ...initialCavityWallState,
        download: state.download,
        screenshots: state.screenshots,
      }
    },
    resetConfigurationToWallType: (state) => {
      const draft = {
        ...initialCavityWallState,
        download: state.download,
        configuration: {
          ...initialCavityWallState.configuration,
          wallType: state.configuration.wallType,
        },
        screenshots: state.screenshots,
      }
      return draft
    },
    resetConfigurationToWallInsulation: (state) => {
      const draft = {
        ...initialCavityWallState,
        download: state.download,
        configuration: {
          ...initialCavityWallState.configuration,
          wallType: state.configuration.wallType,
          wallInsulation: state.configuration.wallInsulation,
        },
        screenshots: state.screenshots,
      }
      return draft
    },
    setThermalPerformance: (
      state,
      action: PayloadAction<CavityWallThermalPerformanceState>
    ) => {
      state.thermalPerformance = action.payload
    },
    setWallTypeType: (state, action: PayloadAction<any>) => {
      const material = FE_MATERIALS.find(
        (material) =>
          material.id === cavityWallMaterialMap[action.payload as WallTypeTypes]
      )!
      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          wallType: {
            ...state.configuration.wallType,
            type: action.payload,
            material: material,
          },
        },
      }
      return draft
    },
    setWallTypeTypeWithThickness: (
      state,
      action: PayloadAction<{ type: any; thickness: string }>
    ) => {
      const { type, thickness: payloadThickness } = action.payload
      const material = FE_MATERIALS.find(
        (material) =>
          material.id === cavityWallMaterialMap[type as WallTypeTypes]
      )!

      const thickness = isNaN(parseInt(payloadThickness))
        ? 0
        : parseInt(payloadThickness)
      let thicknessIsValid = false
      if (thickness && thickness.toString().length <= 3) {
        thicknessIsValid = true
      }

      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          wallType: {
            ...state.configuration.wallType,
            type: type,
            material: {
              ...material,
              thickness: thicknessIsValid ? thickness : material.thickness,
            },
          },
        },
      }
      return draft
    },
    setWallInsulationType: (state, action: PayloadAction<any>) => {
      const material = FE_MATERIALS.find(
        (material) =>
          material.id ===
          cavityWallMaterialMap[action.payload as WallInsulationTypes]
      )!
      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          wallInsulation: {
            ...state.configuration.wallInsulation,
            type: action.payload,
            material: material,
          },
        },
      }
      return draft
    },
    setWallInsulationTypeWithThickness: (
      state,
      action: PayloadAction<{ type: any; thickness: string }>
    ) => {
      const { type, thickness: payloadThickness } = action.payload
      const material = FE_MATERIALS.find(
        (material) =>
          material.id === cavityWallMaterialMap[type as WallInsulationTypes]
      )!

      const thickness = isNaN(parseInt(payloadThickness))
        ? 0
        : parseInt(payloadThickness)
      let thicknessIsValid = false
      if (
        thickness &&
        thickness >= CAVITY_WALL_MINIMUM_INSULATION_THICKNESS &&
        thickness.toString().length <= 3
      ) {
        thicknessIsValid = true
      }

      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          wallInsulation: {
            ...state.configuration.wallInsulation,
            type: type,
            material: {
              ...material,
              thickness: thicknessIsValid ? thickness : material.thickness,
            },
          },
        },
      }
      return draft
    },
    setOuterCavityLeafType: (state, action: PayloadAction<any>) => {
      const material = FE_MATERIALS.find(
        (material) =>
          material.id ===
          cavityWallMaterialMap[action.payload as OuterCavityLeafTypes]
      )!
      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          outerCavityLeaf: {
            ...state.configuration.outerCavityLeaf,
            type: action.payload,
            material: material,
          },
        },
      }
      return draft
    },
    setOuterCavityLeafTypeWithThickness: (
      state,
      action: PayloadAction<{ type: any; thickness: string }>
    ) => {
      const { type, thickness: payloadThickness } = action.payload
      const material = FE_MATERIALS.find(
        (material) =>
          material.id === cavityWallMaterialMap[type as OuterCavityLeafTypes]
      )!

      const thickness = isNaN(parseInt(payloadThickness))
        ? 0
        : parseInt(payloadThickness)
      let thicknessIsValid = false
      if (thickness && thickness.toString().length <= 3) {
        thicknessIsValid = true
      }

      const draft = {
        ...state,
        configuration: {
          ...state.configuration,
          outerCavityLeaf: {
            ...state.configuration.outerCavityLeaf,
            type: type,
            material: {
              ...material,
              thickness: thicknessIsValid ? thickness : material.thickness,
            },
          },
        },
      }
      return draft
    },
    setWallTypeMaterial: (state, action: PayloadAction<FEMaterial>) => {
      const material = action.payload
      state.configuration.wallType.material = material
      state.thermalPerformance = updateThermalLayer(
        state.thermalPerformance,
        material,
        "wallType"
      )
    },
    setWallInsulationMaterial: (state, action: PayloadAction<FEMaterial>) => {
      const material = action.payload
      state.configuration.wallInsulation.material = material
      state.thermalPerformance = updateThermalLayer(
        state.thermalPerformance,
        material,
        "wallInsulation"
      )
    },
    setOuterCavityLeafMaterial: (state, action: PayloadAction<FEMaterial>) => {
      const material = action.payload
      state.configuration.outerCavityLeaf.material = material
      state.thermalPerformance = updateThermalLayer(
        state.thermalPerformance,
        material,
        "outerCavityLeaf"
      )
    },
    setConfiguration(
      state,
      action: PayloadAction<CavityWallConfigurationState>
    ) {
      state.configuration = action.payload
      state.thermalPerformance = getThermalPerformance(action.payload)
    },
    setHumanReadableName: (state, action: PayloadAction<string>) => {
      state.configuration.humanReadableName = action.payload
      state.thermalPerformance = getThermalPerformance(state.configuration)
    },
    setDownloadState: (state, action: PayloadAction<DownloadState>) => {
      state.download = action.payload
    },
    addScreenshot: (state, action: PayloadAction<string>) => {
      state.screenshots = [...state.screenshots, action.payload]
    },
    setOverviewScreenshot: (state, action: PayloadAction<string>) => {
      state.overviewScreenshot = action.payload
    },
  },
})
